import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { HOUSEKEEPING_ACCENT } from '../../utils/housekeepingMobileLayout';

const THEME_COLOR = HOUSEKEEPING_ACCENT;

/** Borderless "soft filled" input style — no outline / focus ring. */
export const getSoftFieldSx = (isDark) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
    transition: 'background-color 0.2s ease',
    '& fieldset': {
      border: 'none',
      borderWidth: 0,
    },
    '&:hover fieldset': {
      border: 'none',
      borderWidth: 0,
    },
    '&.Mui-focused fieldset': {
      border: 'none',
      borderWidth: 0,
    },
    '&.Mui-error fieldset': {
      border: 'none',
      borderWidth: 0,
    },
    '&:hover': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
    },
    '&.Mui-focused': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
      boxShadow: 'none',
    },
    '&.Mui-error': {
      backgroundColor: isDark ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)',
      boxShadow: 'none',
    },
  },
  '& .MuiInputLabel-root': {
    color: isDark ? '#8a939e' : '#78828c',
    '&.Mui-focused': { color: isDark ? '#8a939e' : '#78828c' },
    '&.Mui-error': { color: '#f44336' },
  },
});

export const SectionLabel = ({ children, required, isDark }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      mb: 1,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontSize: '0.68rem',
      color: isDark ? '#8a939e' : '#78828c',
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: '#f44336', ml: 0.5 }}>*</Box>}
  </Typography>
);

export const SelectableChip = ({ label, icon, selected, color, isDark, onClick, size = 'medium' }) => (
  <Chip
    label={label}
    icon={icon}
    onClick={onClick}
    clickable
    size={size}
    sx={{
      borderRadius: '10px',
      fontWeight: selected ? 700 : 500,
      px: 0.5,
      transition: 'all 0.18s ease',
      border: '1.5px solid',
      borderColor: selected ? color : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'),
      backgroundColor: selected
        ? alpha(color, isDark ? 0.22 : 0.12)
        : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
      color: selected ? color : (isDark ? '#aab2bc' : '#5a646e'),
      boxShadow: selected ? `0 0 12px ${alpha(color, 0.35)}` : 'none',
      transform: selected ? 'translateY(-1px)' : 'none',
      '& .MuiChip-icon': { color: selected ? color : (isDark ? '#7c858f' : '#9aa3ac') },
      '&:hover': {
        backgroundColor: selected
          ? alpha(color, isDark ? 0.3 : 0.18)
          : alpha(color, isDark ? 0.12 : 0.07),
        borderColor: color,
      },
    }}
  />
);

/** Rounded icon badge + title/subtitle used in housekeeping drawer headers. */
export const DrawerHeaderTitle = ({ icon, title, subtitle, isDark }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? '#1f2a28' : '#e6f9f2',
        color: THEME_COLOR,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" sx={{ color: isDark ? '#eee' : '#222', fontWeight: 700, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ color: isDark ? '#8a939e' : '#78828c' }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

export const getDrawerPrimaryButtonSx = () => ({
  borderRadius: '12px',
  background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #35c9a0 100%)`,
  color: '#04211a',
  fontWeight: 700,
  boxShadow: `0 4px 16px ${alpha(THEME_COLOR, 0.35)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #35c9a0 100%)`,
    boxShadow: `0 6px 20px ${alpha(THEME_COLOR, 0.45)}`,
    opacity: 0.95,
  },
});

export const getDrawerCancelButtonSx = (isDark, surfaceBorder) => ({
  borderRadius: '12px',
  borderColor: surfaceBorder,
  color: isDark ? '#ddd' : '#666',
  fontWeight: 600,
  '&:hover': {
    borderColor: THEME_COLOR,
    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
    color: THEME_COLOR,
  },
});
